import JSZip from "jszip";
import {
  WorkspaceItem,
  DriveLoadedContent,
  DriveExtractedImage,
  DriveLoadedSourceType,
} from "../types";

const DRIVE_API_BASE = "https://www.googleapis.com/drive/v3";
const DRIVE_UPLOAD_BASE = "https://www.googleapis.com/upload/drive/v3";

const MAX_TREE_DEPTH = 6;
const MAX_TREE_NODES = 3000;
const MAX_FILE_TEXT_CHARS = 18000;
const MAX_EXTRACTED_IMAGES_PER_FILE = 3;
// Brand Kit logos, approved OG examples, and website screenshots are often larger than 2MB.
// Keep this high enough for visual references while still bounded before chat payload packing.
const MAX_INLINE_IMAGE_BYTES = 5 * 1024 * 1024;

// Phase 12 payload discipline:
// Checked/gold Drive scope may grow forever. That scope is the allowed knowledge pool.
// The chat request payload must stay small enough to send. These limits keep one focused
// folder from pushing every large doc/image/PDF inline and causing HTTP 413.
const MAX_CHAT_PROMPT_TOTAL_TEXT_CHARS = 120000;
const MAX_CHAT_PROMPT_FILE_COUNT = 36;
const MAX_CHAT_PROMPT_VISUAL_PAYLOAD_COUNT = 10;
const MAX_CHAT_PROMPT_TOTAL_BASE64_CHARS = 16000000;
const MAX_CHAT_PROMPT_SINGLE_BASE64_CHARS = 7000000;

// Browser-side Drive reads must never hang the chat UI or background memory warmup forever.
const DRIVE_FETCH_TIMEOUT_MS = 30000;
const DRIVE_FETCH_RETRY_LIMIT = 3;
const DRIVE_RETRY_STATUSES = new Set([429, 500, 502, 503, 504]);

const GOOGLE_FOLDER_MIME = "application/vnd.google-apps.folder";
const GOOGLE_SHORTCUT_MIME = "application/vnd.google-apps.shortcut";

/**
 * Drive contract:
 * - fetchDriveFolderRecursively() is TREE METADATA ONLY.
 * - It must never download/read every file while building the sidebar.
 * - checked/gold = focused RodgersPC shop-memory scope.
 * - unchecked = ignored.
 * - downloadDriveLoadedContent() is the full reader used by direct pulls and background warmup.
 * - background warmup must be safe: bounded file size, timeout, retry, and clear extraction status.
 * - downloadDriveFileContents() remains as text-only compatibility wrapper.
 *
 * Drive Brain rule:
 * when a relevant/focused file is loaded, read/use everything useful from that file:
 * document text, DOCX embedded images, image files, PDFs as vision payloads,
 * and text-readable formats. App.tsx/server.ts decide what gets passed to Gemini.
 */

interface GDriveAPIFile {
  id: string;
  name: string;
  mimeType: string;
  size?: string;
  parents?: string[];
  modifiedTime?: string;
  webViewLink?: string;
  iconLink?: string;
  driveId?: string;
  shortcutDetails?: {
    targetId?: string;
    targetMimeType?: string;
  };
}

function assertAccessToken(accessToken: string) {
  if (!accessToken || accessToken.trim() === "") {
    throw new Error("Missing Google Drive access token. Sign in again to reconnect Drive.");
  }
}

function escapeDriveQueryValue(value: string): string {
  return String(value || "").replace(/\\/g, "\\\\").replace(/'/g, "\\'");
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRetriableDriveError(err: unknown): boolean {
  const raw = String((err as any)?.message || (err as any)?.name || err || "").toLowerCase();

  return (
    raw.includes("abort") ||
    raw.includes("timeout") ||
    raw.includes("network") ||
    raw.includes("failed to fetch") ||
    raw.includes("load failed") ||
    raw.includes("temporarily") ||
    raw.includes("quota") ||
    raw.includes("rate")
  );
}

async function driveFetch(
  accessToken: string,
  url: string,
  init: RequestInit = {},
): Promise<Response> {
  assertAccessToken(accessToken);

  const incomingHeaders = new Headers(init.headers || {});
  incomingHeaders.set("Authorization", `Bearer ${accessToken}`);

  if (!incomingHeaders.has("Accept")) {
    incomingHeaders.set("Accept", "application/json,text/plain,*/*");
  }

  const method = String(init.method || "GET").toUpperCase();
  const canRetry = method === "GET" || method === "HEAD";
  const maxAttempts = canRetry ? DRIVE_FETCH_RETRY_LIMIT : 1;
  let lastError: unknown = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const controller = new AbortController();
    const externalSignal = init.signal;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const abortFromExternalSignal = () => controller.abort();

    try {
      if (externalSignal?.aborted) {
        throw new Error("Drive request aborted before it started.");
      }

      if (externalSignal) {
        externalSignal.addEventListener("abort", abortFromExternalSignal, { once: true });
      }

      timeoutId = setTimeout(() => controller.abort(), DRIVE_FETCH_TIMEOUT_MS);

      const response = await fetch(url, {
        ...init,
        signal: controller.signal,
        headers: incomingHeaders,
      });

      if (
        canRetry &&
        DRIVE_RETRY_STATUSES.has(response.status) &&
        attempt < maxAttempts
      ) {
        // Drain body so the browser can release the connection before retrying.
        await response.text().catch(() => "");
        await sleep(450 * attempt);
        continue;
      }

      return response;
    } catch (err) {
      lastError = err;

      if (canRetry && attempt < maxAttempts && isRetriableDriveError(err)) {
        await sleep(450 * attempt);
        continue;
      }

      throw err;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      if (externalSignal) {
        externalSignal.removeEventListener("abort", abortFromExternalSignal);
      }
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error(`Google Drive request failed after ${maxAttempts} attempts.`);
}

async function readDriveError(response: Response): Promise<string> {
  let body = "";

  try {
    body = await response.text();
  } catch {
    body = "";
  }

  if (!body) {
    return `Google Drive API returned HTTP ${response.status}: ${response.statusText}`;
  }

  try {
    const parsed = JSON.parse(body);
    const message = parsed?.error?.message || parsed?.message || body;
    return `Google Drive API returned HTTP ${response.status}: ${message}`;
  } catch {
    return `Google Drive API returned HTTP ${response.status}: ${body}`;
  }
}

function truncateText(text: string, maxChars = MAX_FILE_TEXT_CHARS): string {
  if (!text) return "";
  if (text.length <= maxChars) return text;

  return `${text.slice(0, maxChars)}\n\n[Drive file content truncated automatically to protect app performance.]`;
}

function normalizeExtractedText(text: string): string {
  return String(text || "")
    .replace(/\r/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function safeParseInt(value?: string): number {
  if (!value) return 0;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunkSize = 0x8000;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const clean = String(base64 || "").replace(/\s+/g, "");
  const binary = atob(clean);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes.buffer;
}

function stripHtmlToReadableText(html: string): string {
  let text = String(html || "");

  text = text.replace(/<script[\s\S]*?<\/script>/gi, " ");
  text = text.replace(/<style[\s\S]*?<\/style>/gi, " ");
  text = text.replace(/<noscript[\s\S]*?<\/noscript>/gi, " ");
  text = text.replace(/<\/(p|div|li|tr|td|th|h1|h2|h3|h4|section|article)>/gi, "\n");
  text = text.replace(/<br\s*\/?>/gi, "\n");
  text = text.replace(/<[^>]+>/g, " ");

  return normalizeExtractedText(decodeXmlEntities(text));
}

function looksLikeHtmlDocument(text: string): boolean {
  const lower = String(text || "").slice(0, 4000).toLowerCase();
  return lower.includes("<html") || lower.includes("<body") || lower.includes("<!doctype") || lower.includes("<p") || lower.includes("<div") || lower.includes("<img");
}

function extractDataUrlVisionPayloadsFromText(args: {
  text: string;
  fileId: string;
  filePath: string;
  warnings: string[];
  maxImages?: number;
}): DriveExtractedImage[] {
  const images: DriveExtractedImage[] = [];
  const source = String(args.text || "");
  const maxImages = args.maxImages ?? MAX_EXTRACTED_IMAGES_PER_FILE;
  let sawDataUrl = false;
  const regex = /data:(image\/(?:png|jpeg|jpg|webp|gif|bmp|tiff|heic|heif));base64,([A-Za-z0-9+/=\s]+)(?=["')\s>])/gi;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(source)) !== null && images.length < maxImages) {
    sawDataUrl = true;
    const mimeType = match[1] === "image/jpg" ? "image/jpeg" : match[1];
    const base64 = String(match[2] || "").replace(/\s+/g, "");

    if (!base64 || base64.includes("[Payload size optimized]")) {
      args.warnings.push("Skipped embedded image data URL because it was empty or already payload-optimized.");
      continue;
    }

    try {
      const buffer = base64ToArrayBuffer(base64);
      const payload = makeVisionPayload({
        buffer,
        fileId: args.fileId,
        filePath: args.filePath,
        name: `embedded-image-${images.length + 1}.${mimeType.split("/")[1] || "png"}`,
        mimeType,
        source: "docx_embedded",
        warnings: args.warnings,
      });

      if (payload) images.push(payload);
    } catch (err: any) {
      args.warnings.push(`Could not recover embedded data-url image ${images.length + 1}: ${err?.message || err}`);
    }
  }

  if (sawDataUrl && images.length >= maxImages) {
    args.warnings.push(`Embedded image recovery capped at ${maxImages} image(s).`);
  }

  return images;
}

async function readLegacyDocLikeFile(
  accessToken: string,
  meta: GDriveAPIFile,
  resolvedName: string,
  resolvedId: string,
  filePath: string,
): Promise<DriveLoadedContent> {
  const warnings: string[] = [
    "Legacy .doc/application-msword file handled with best-effort HTML/text extraction. True binary Word .doc is still not fully supported.",
  ];

  try {
    const buffer = await downloadBinaryFile(accessToken, resolvedId);
    const utf8Text = new TextDecoder("utf-8", { fatal: false }).decode(buffer);
    const latinText = new TextDecoder("latin1").decode(buffer);
    const candidate = utf8Text.replace(/\u0000/g, " ").trim().length >= latinText.replace(/\u0000/g, " ").trim().length
      ? utf8Text
      : latinText;

    const embeddedImages = extractDataUrlVisionPayloadsFromText({
      text: candidate,
      fileId: resolvedId,
      filePath,
      warnings,
      maxImages: MAX_EXTRACTED_IMAGES_PER_FILE,
    });

    let readableText = "";

    if (looksLikeHtmlDocument(candidate)) {
      readableText = stripHtmlToReadableText(candidate);
    } else {
      readableText = normalizeExtractedText(
        candidate
          .replace(/\u0000/g, " ")
          .replace(/[^\x09\x0A\x0D\x20-\x7E\u00A0-\uFFFF]+/g, " ")
      );
    }

    // Binary .doc can decode into meaningless garbage. Require a minimum amount of readable words.
    const readableWordCount = (readableText.match(/[A-Za-z0-9]{2,}/g) || []).length;

    if (readableWordCount < 20 && embeddedImages.length === 0) {
      warnings.push(`${resolvedName} did not decode as readable HTML/text. Convert it to Google Docs, DOCX, PDF, or HTML for reliable Drive Brain use.`);
      readableText = "";
    }

    return makeLoadedContent({
      meta,
      resolvedName,
      resolvedId,
      path: filePath,
      sourceType: "binary",
      content: readableText,
      images: embeddedImages,
      warnings,
      status: readableText.trim() || embeddedImages.length > 0 ? "partial" : "failed",
    });
  } catch (err: any) {
    return makeLoadedContent({
      meta,
      resolvedName,
      resolvedId,
      path: filePath,
      sourceType: "binary",
      content: "",
      images: [],
      warnings: [...warnings, `Legacy .doc best-effort extraction failed: ${err?.message || err}`],
      status: "failed",
    });
  }
}

function extFromName(fileName: string): string {
  const lower = String(fileName || "").toLowerCase();
  const idx = lower.lastIndexOf(".");
  return idx >= 0 ? lower.slice(idx) : "";
}

function mimeFromImageName(fileName: string): string {
  const ext = extFromName(fileName);

  if (ext === ".png") return "image/png";
  if (ext === ".jpg" || ext === ".jpeg") return "image/jpeg";
  if (ext === ".gif") return "image/gif";
  if (ext === ".webp") return "image/webp";
  if (ext === ".bmp") return "image/bmp";
  if (ext === ".tif" || ext === ".tiff") return "image/tiff";
  if (ext === ".svg") return "image/svg+xml";

  return "application/octet-stream";
}


function normalizeVisionMime(fileName: string, mimeType: string): string {
  const mime = String(mimeType || "").toLowerCase().trim();
  const fromName = mimeFromImageName(fileName);

  if (mime.startsWith("image/")) {
    // Google/Gemini vision handles common raster formats best. Preserve the
    // source MIME when it is already usable, otherwise fall back to extension.
    return mime;
  }

  if (mime === "application/pdf") return "application/pdf";

  return fromName && fromName !== "application/octet-stream" ? fromName : mime || "application/octet-stream";
}

function isInlineVisionSupportedMime(mimeType: string): boolean {
  const mime = String(mimeType || "").toLowerCase().trim();

  return (
    mime === "application/pdf" ||
    mime === "image/png" ||
    mime === "image/jpeg" ||
    mime === "image/jpg" ||
    mime === "image/webp" ||
    mime === "image/gif" ||
    mime === "image/bmp" ||
    mime === "image/tiff" ||
    mime === "image/heic" ||
    mime === "image/heif"
  );
}

function makeVisionPayload(args: {
  buffer: ArrayBuffer;
  fileId: string;
  filePath: string;
  name: string;
  mimeType: string;
  source: DriveExtractedImage["source"];
  warnings: string[];
}): DriveExtractedImage | null {
  const sizeBytes = args.buffer.byteLength;
  const mimeType = normalizeVisionMime(args.name, args.mimeType);

  if (sizeBytes <= 0) {
    args.warnings.push(`Skipped ${args.name}: Drive returned an empty file payload.`);
    return null;
  }

  if (sizeBytes > MAX_INLINE_IMAGE_BYTES) {
    args.warnings.push(`Skipped ${args.name}: ${sizeBytes} bytes is too large for inline model vision payload.`);
    return null;
  }

  if (!isInlineVisionSupportedMime(mimeType)) {
    args.warnings.push(`Skipped ${args.name}: ${mimeType || "unknown MIME"} is not a supported inline vision MIME type.`);
    return null;
  }

  return {
    id: `${args.fileId}:${args.source}:${args.name}`,
    name: args.name,
    mimeType,
    base64: arrayBufferToBase64(args.buffer),
    source: args.source,
    fileId: args.fileId,
    filePath: args.filePath,
    sizeBytes,
  };
}

function isImageMimeOrName(fileName: string, mimeType: string): boolean {
  const lowerMime = String(mimeType || "").toLowerCase();
  const ext = extFromName(fileName);

  return (
    lowerMime.startsWith("image/") ||
    [".png", ".jpg", ".jpeg", ".gif", ".webp", ".bmp", ".tif", ".tiff", ".svg"].includes(ext)
  );
}

function isProbablyTextFile(fileName: string, mimeType: string): boolean {
  const lowerName = fileName.toLowerCase();
  const lowerMime = mimeType.toLowerCase();

  if (lowerMime.startsWith("text/")) return true;
  if (lowerMime.includes("json")) return true;
  if (lowerMime.includes("xml")) return true;
  if (lowerMime.includes("csv")) return true;
  if (lowerMime.includes("html")) return true;
  if (lowerMime.includes("javascript")) return true;
  if (lowerMime.includes("typescript")) return true;

  return [
    ".txt",
    ".md",
    ".csv",
    ".json",
    ".xml",
    ".html",
    ".htm",
    ".css",
    ".js",
    ".ts",
    ".tsx",
    ".jsx",
    ".log",
    ".ini",
    ".yml",
    ".yaml",
    ".ps1",
    ".bat",
    ".cmd",
    ".reg",
  ].some((ext) => lowerName.endsWith(ext));
}

function isDocxFile(fileName: string, mimeType: string): boolean {
  const lowerName = String(fileName || "").toLowerCase();
  const lowerMime = String(mimeType || "").toLowerCase();

  return (
    lowerName.endsWith(".docx") ||
    lowerMime.includes("wordprocessingml.document") ||
    lowerMime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  );
}

function isLegacyDocFile(fileName: string, mimeType: string): boolean {
  const lowerName = String(fileName || "").toLowerCase();
  const lowerMime = String(mimeType || "").toLowerCase();

  return lowerName.endsWith(".doc") || lowerMime === "application/msword";
}

function isPdfFile(fileName: string, mimeType: string): boolean {
  const lowerName = String(fileName || "").toLowerCase();
  const lowerMime = String(mimeType || "").toLowerCase();

  return lowerName.endsWith(".pdf") || lowerMime === "application/pdf";
}

function getSourceType(fileName: string, mimeType: string): DriveLoadedSourceType {
  const lowerMime = String(mimeType || "").toLowerCase();

  if (lowerMime === "application/vnd.google-apps.document") return "google_doc";
  if (lowerMime === "application/vnd.google-apps.spreadsheet") return "spreadsheet";
  if (lowerMime === "application/vnd.google-apps.presentation") return "google_doc";
  if (isDocxFile(fileName, mimeType)) return "docx";
  if (isPdfFile(fileName, mimeType)) return "pdf";
  if (isImageMimeOrName(fileName, mimeType)) return "image";
  if (isProbablyTextFile(fileName, mimeType)) return "text";

  return "unknown";
}

function decodeXmlEntities(input: string): string {
  return String(input || "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, code) => {
      const parsed = Number.parseInt(code, 10);
      return Number.isFinite(parsed) ? String.fromCharCode(parsed) : _;
    })
    .replace(/&#x([0-9a-fA-F]+);/g, (_, code) => {
      const parsed = Number.parseInt(code, 16);
      return Number.isFinite(parsed) ? String.fromCharCode(parsed) : _;
    });
}

function xmlToPlainText(xml: string): string {
  if (!xml) return "";

  return normalizeExtractedText(
    decodeXmlEntities(
      xml
        .replace(/<w:tab\s*\/?>(?:<\/w:tab>)?/gi, "\t")
        .replace(/<w:br\s*\/?>/gi, "\n")
        .replace(/<w:cr\s*\/?>/gi, "\n")
        .replace(/<w:pict[\s\S]*?<\/w:pict>/gi, "\n[Embedded image]\n")
        .replace(/<w:drawing[\s\S]*?<\/w:drawing>/gi, "\n[Embedded image]\n")
        .replace(/<a:blip[^>]+r:embed="([^"]+)"[^>]*>/gi, "\n[Embedded image relationship: $1]\n")
        .replace(/<w:t[^>]*>/gi, "")
        .replace(/<\/w:t>/gi, "")
        .replace(/<\/w:p>/gi, "\n")
        .replace(/<\/w:tr>/gi, "\n")
        .replace(/<\/w:tc>/gi, "\t")
        .replace(/<[^>]+>/g, ""),
    ),
  );
}

async function extractDocxPackage(
  buffer: ArrayBuffer,
  resolvedName: string,
  fileId: string,
  filePath: string,
): Promise<{ text: string; images: DriveExtractedImage[]; warnings: string[] }> {
  const warnings: string[] = [];
  const textSections: string[] = [];
  const images: DriveExtractedImage[] = [];

  try {
    const zip = await JSZip.loadAsync(buffer);

    const candidateTextParts = [
      "word/document.xml",
      "word/footnotes.xml",
      "word/endnotes.xml",
      "word/comments.xml",
    ];

    for (let i = 1; i <= 50; i++) {
      candidateTextParts.push(`word/header${i}.xml`);
      candidateTextParts.push(`word/footer${i}.xml`);
    }

    for (const partPath of candidateTextParts) {
      const entry = zip.file(partPath);
      if (!entry) continue;

      const xml = await entry.async("string");
      const text = xmlToPlainText(xml);
      if (text) {
        textSections.push(`--- ${partPath} ---\n${text}`);
      }
    }

    const mediaEntries = Object.values(zip.files as Record<string, any>)
      .filter((entry: any) => !entry.dir && String(entry.name || "").toLowerCase().startsWith("word/media/"))
      .slice(0, MAX_EXTRACTED_IMAGES_PER_FILE);

    for (const entry of mediaEntries) {
      const mediaEntry: any = entry;
      try {
        const raw = await mediaEntry.async("arraybuffer");

        const imageName = String(mediaEntry.name || "").split("/").pop() || entry.name;
        const payload = makeVisionPayload({
          buffer: raw,
          fileId,
          filePath,
          name: imageName,
          mimeType: mimeFromImageName(imageName),
          source: "docx_embedded",
          warnings,
        });
        if (payload) images.push(payload);
      } catch (imgErr: any) {
        warnings.push(`Could not extract embedded DOCX image ${mediaEntry.name}: ${imgErr?.message || imgErr}`);
      }
    }

    const text = normalizeExtractedText(textSections.join("\n\n"));

    if (!text && images.length === 0) {
      warnings.push(`${resolvedName} was downloaded as DOCX, but no readable text or embedded images were found.`);
    }

    if (mediaEntries.length >= MAX_EXTRACTED_IMAGES_PER_FILE) {
      warnings.push(`Embedded image extraction capped at ${MAX_EXTRACTED_IMAGES_PER_FILE} images for this file.`);
    }

    return {
      text: truncateText(text || ""),
      images,
      warnings,
    };
  } catch (err: any) {
    return {
      text: `[Drive DOCX extraction failed: ${resolvedName} could not be opened as a DOCX package. ${err?.message || err}]`,
      images: [],
      warnings: [`DOCX extraction failed for ${resolvedName}: ${err?.message || err}`],
    };
  }
}

function extractPdfTextNaive(buffer: ArrayBuffer, resolvedName: string): string {
  const bytes = new Uint8Array(buffer);
  const decoder = new TextDecoder("latin1");
  const raw = decoder.decode(bytes);

  const chunks: string[] = [];
  const literalStringPattern = /\(([^()]{3,500})\)\s*Tj/g;
  const arrayStringPattern = /\[((?:\s*\([^()]{1,300}\)\s*)+)\]\s*TJ/g;

  let match: RegExpExecArray | null;
  while ((match = literalStringPattern.exec(raw)) && chunks.length < 5000) {
    chunks.push(match[1]);
  }

  while ((match = arrayStringPattern.exec(raw)) && chunks.length < 5000) {
    const inner = match[1];
    const parts = [...inner.matchAll(/\(([^()]*)\)/g)].map((m) => m[1]);
    if (parts.length > 0) chunks.push(parts.join(""));
  }

  const text = normalizeExtractedText(
    chunks
      .join("\n")
      .replace(/\\n/g, "\n")
      .replace(/\\r/g, "\n")
      .replace(/\\t/g, "\t")
      .replace(/\\\(/g, "(")
      .replace(/\\\)/g, ")")
      .replace(/\\\\/g, "\\"),
  );

  if (!text) {
    return `[Drive PDF text extraction note: ${resolvedName} may be scanned, image-based, or compressed. The full PDF is attached as a vision payload when App.tsx/server.ts pass Drive images forward.]`;
  }

  return truncateText(text);
}

async function downloadBinaryFile(accessToken: string, fileId: string): Promise<ArrayBuffer> {
  const downloadUrl = `${DRIVE_API_BASE}/files/${encodeURIComponent(fileId)}?alt=media&supportsAllDrives=true`;
  const response = await driveFetch(accessToken, downloadUrl, {
    headers: {
      Accept: "application/octet-stream,*/*",
    },
  });

  if (!response.ok) {
    throw new Error(await readDriveError(response));
  }

  return await response.arrayBuffer();
}

async function exportGoogleFile(
  accessToken: string,
  fileId: string,
  exportMime: string,
): Promise<Response> {
  const exportUrl =
    `${DRIVE_API_BASE}/files/${encodeURIComponent(fileId)}/export` +
    `?mimeType=${encodeURIComponent(exportMime)}`;

  return driveFetch(accessToken, exportUrl, {
    headers: {
      Accept: `${exportMime},text/plain,*/*`,
    },
  });
}

export type DriveBrowserDownloadResult = {
  fileId: string;
  fileName: string;
  mimeType: string;
  sourceMimeType: string;
  sizeBytes: number;
  exported: boolean;
};

function stripKnownExtension(fileName: string): string {
  const name = String(fileName || "Drive file").trim() || "Drive file";
  return name.replace(/\.(docx|xlsx|pptx|pdf|png|txt|csv)$/i, "");
}

function safeDownloadName(fileName: string): string {
  return (String(fileName || "Drive_File").trim() || "Drive_File")
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, " ")
    .slice(0, 180);
}

function getGoogleWorkspaceDownloadFormat(meta: GDriveAPIFile): {
  exportMime: string;
  extension: string;
  fallbackExtension?: string;
} {
  switch (meta.mimeType) {
    case "application/vnd.google-apps.document":
      return {
        exportMime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        extension: ".docx",
        fallbackExtension: ".txt",
      };
    case "application/vnd.google-apps.spreadsheet":
      return {
        exportMime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        extension: ".xlsx",
        fallbackExtension: ".csv",
      };
    case "application/vnd.google-apps.presentation":
      return {
        exportMime: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        extension: ".pptx",
        fallbackExtension: ".pdf",
      };
    case "application/vnd.google-apps.drawing":
      return {
        exportMime: "image/png",
        extension: ".png",
        fallbackExtension: ".pdf",
      };
    default:
      return {
        exportMime: "application/pdf",
        extension: ".pdf",
        fallbackExtension: ".txt",
      };
  }
}

function triggerBrowserDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = safeDownloadName(fileName);
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
}

/**
 * Downloads an existing Google Drive file to the local browser.
 *
 * Rules:
 * - Regular uploaded files download as their original blob.
 * - Google Docs export as DOCX.
 * - Google Sheets export as XLSX.
 * - Google Slides export as PPTX.
 * - Google Drawings export as PNG.
 * - Shortcuts are resolved before download/export.
 */
export async function downloadDriveFile(
  accessToken: string,
  fileId: string,
  fileName = "Drive file",
): Promise<DriveBrowserDownloadResult> {
  assertAccessToken(accessToken);

  let meta = await fetchFileMetadata(accessToken, fileId);
  meta = await resolveShortcut(accessToken, meta);

  const resolvedId = meta.id || fileId;
  const sourceMimeType = meta.mimeType || "";
  const baseName = meta.name || fileName || "Drive file";

  if (sourceMimeType === GOOGLE_FOLDER_MIME) {
    throw new Error("Download is only available for files, not folders.");
  }

  if (sourceMimeType.startsWith("application/vnd.google-apps.")) {
    const format = getGoogleWorkspaceDownloadFormat(meta);
    let response = await exportGoogleFile(accessToken, resolvedId, format.exportMime);
    let finalMime = format.exportMime;
    let finalExtension = format.extension;

    if (!response.ok && format.fallbackExtension) {
      const fallbackMime =
        format.fallbackExtension === ".txt"
          ? "text/plain"
          : format.fallbackExtension === ".csv"
            ? "text/csv"
            : "application/pdf";

      response = await exportGoogleFile(accessToken, resolvedId, fallbackMime);
      finalMime = fallbackMime;
      finalExtension = format.fallbackExtension;
    }

    if (!response.ok) {
      throw new Error(await readDriveError(response));
    }

    const blob = await response.blob();
    const finalName = safeDownloadName(`${stripKnownExtension(baseName)}${finalExtension}`);
    triggerBrowserDownload(blob, finalName);

    return {
      fileId: resolvedId,
      fileName: finalName,
      mimeType: finalMime,
      sourceMimeType,
      sizeBytes: blob.size,
      exported: true,
    };
  }

  const downloadUrl = `${DRIVE_API_BASE}/files/${encodeURIComponent(resolvedId)}?alt=media&supportsAllDrives=true`;
  const response = await driveFetch(accessToken, downloadUrl, {
    headers: {
      Accept: `${sourceMimeType || "application/octet-stream"},application/octet-stream,*/*`,
    },
  });

  if (!response.ok) {
    throw new Error(await readDriveError(response));
  }

  const blob = await response.blob();
  const finalName = safeDownloadName(baseName || fileName || "Drive_File");
  triggerBrowserDownload(blob, finalName);

  return {
    fileId: resolvedId,
    fileName: finalName,
    mimeType: blob.type || sourceMimeType || "application/octet-stream",
    sourceMimeType,
    sizeBytes: blob.size,
    exported: false,
  };
}

export function extractFolderId(input: string): string {
  const trimmed = (input || "").trim();

  if (!trimmed) return "root";
  if (trimmed === "root") return "root";

  const folderMatch = trimmed.match(/\/folders\/([a-zA-Z0-9-_]+)/);
  if (folderMatch?.[1]) return folderMatch[1];

  const fileMatch = trimmed.match(/\/file\/d\/([a-zA-Z0-9-_]+)/);
  if (fileMatch?.[1]) return fileMatch[1];

  const docMatch = trimmed.match(/\/(?:document|spreadsheets|presentation)\/d\/([a-zA-Z0-9-_]+)/);
  if (docMatch?.[1]) return docMatch[1];

  const idQueryMatch = trimmed.match(/[?&]id=([a-zA-Z0-9-_]+)/);
  if (idQueryMatch?.[1]) return idQueryMatch[1];

  if (!trimmed.includes("/") && !trimmed.includes(" ")) {
    return trimmed;
  }

  return "root";
}

async function fetchFileMetadata(accessToken: string, fileId: string): Promise<GDriveAPIFile> {
  const params = new URLSearchParams({
    fields: "id,name,mimeType,size,parents,modifiedTime,webViewLink,iconLink,driveId,shortcutDetails",
    supportsAllDrives: "true",
  });

  const response = await driveFetch(
    accessToken,
    `${DRIVE_API_BASE}/files/${encodeURIComponent(fileId)}?${params.toString()}`,
  );

  if (!response.ok) {
    throw new Error(await readDriveError(response));
  }

  return await response.json();
}

async function resolveShortcut(accessToken: string, apiFile: GDriveAPIFile): Promise<GDriveAPIFile> {
  if (apiFile.mimeType !== GOOGLE_SHORTCUT_MIME) return apiFile;

  const targetId = apiFile.shortcutDetails?.targetId;
  if (!targetId) return apiFile;

  try {
    const target = await fetchFileMetadata(accessToken, targetId);
    return {
      ...target,
      name: apiFile.name || target.name,
      parents: apiFile.parents || target.parents,
      webViewLink: apiFile.webViewLink || target.webViewLink,
      iconLink: apiFile.iconLink || target.iconLink,
    };
  } catch (err) {
    console.warn(`[Drive Tree] Failed to resolve shortcut ${apiFile.name}:`, err);
    return apiFile;
  }
}

async function resolveFolderId(accessToken: string, inputId: string): Promise<string> {
  const id = extractFolderId(inputId);
  if (id === "root") return "root";

  try {
    const meta = await fetchFileMetadata(accessToken, id);
    const resolved = await resolveShortcut(accessToken, meta);
    return resolved.id || id;
  } catch (err) {
    console.warn("[Drive Tree] Folder/shortcut resolution failed; using supplied ID.", err);
    return id;
  }
}

export async function fetchDriveFolderName(accessToken: string, fileId: string): Promise<string> {
  const id = extractFolderId(fileId);

  if (id === "root") return "My Drive";

  const data = await fetchFileMetadata(accessToken, id);
  const resolved = await resolveShortcut(accessToken, data);
  return resolved.name || data.name || "RodgersPC Command Center";
}

type ListVariant = "normal" | "user" | "allDrives" | "drive" | "sharedWithMe";

async function listChildrenOnce(
  accessToken: string,
  parentId: string,
  variant: ListVariant,
  driveId?: string,
): Promise<GDriveAPIFile[]> {
  const files: GDriveAPIFile[] = [];
  let pageToken = "";

  do {
    const q =
      variant === "sharedWithMe"
        ? `sharedWithMe = true and trashed = false`
        : parentId === "root"
          ? `'root' in parents and trashed = false`
          : `'${escapeDriveQueryValue(parentId)}' in parents and trashed = false`;

    const params = new URLSearchParams({
      q,
      fields:
        "nextPageToken,files(id,name,mimeType,size,parents,modifiedTime,webViewLink,iconLink,driveId,shortcutDetails)",
      pageSize: "1000",
      orderBy: "folder,name_natural",
      supportsAllDrives: "true",
      includeItemsFromAllDrives: "true",
    });

    if (variant === "user") {
      params.set("corpora", "user");
    } else if (variant === "allDrives") {
      params.set("corpora", "allDrives");
    } else if (variant === "drive" && driveId) {
      params.set("corpora", "drive");
      params.set("driveId", driveId);
    }

    if (pageToken) {
      params.set("pageToken", pageToken);
    }

    const response = await driveFetch(accessToken, `${DRIVE_API_BASE}/files?${params.toString()}`);

    if (!response.ok) {
      throw new Error(await readDriveError(response));
    }

    const data = await response.json();
    files.push(...(data.files || []));
    pageToken = data.nextPageToken || "";
  } while (pageToken);

  return files;
}

async function findDriveIdForFolder(accessToken: string, parentId: string): Promise<string | undefined> {
  if (parentId === "root") return undefined;

  try {
    const meta = await fetchFileMetadata(accessToken, parentId);
    return meta.driveId;
  } catch (err) {
    console.warn(`[Drive Tree] Could not determine driveId for ${parentId}:`, err);
    return undefined;
  }
}

async function listChildren(accessToken: string, parentId: string): Promise<GDriveAPIFile[]> {
  const driveId = await findDriveIdForFolder(accessToken, parentId);
  const variants: ListVariant[] = driveId
    ? ["normal", "user", "drive", "allDrives"]
    : ["normal", "user", "allDrives"];
  let lastError: unknown = null;

  for (const variant of variants) {
    try {
      const files = await listChildrenOnce(accessToken, parentId, variant, driveId);
      console.info(`[Drive Tree] ${variant} query returned ${files.length} child node(s) for ${parentId}.`);

      if (files.length > 0) return files;
    } catch (err) {
      lastError = err;
      console.warn(`[Drive Tree] ${variant} query failed for ${parentId}:`, err);
    }
  }

  if (parentId === "root") {
    try {
      const shared = await listChildrenOnce(accessToken, parentId, "sharedWithMe");
      console.info(`[Drive Tree] sharedWithMe fallback returned ${shared.length} node(s).`);
      return shared;
    } catch (err) {
      lastError = err;
      console.warn("[Drive Tree] sharedWithMe fallback failed:", err);
    }
  }

  if (lastError) throw lastError;
  return [];
}

export async function fetchDriveFolderRecursively(
  accessToken: string,
  folderId: string,
  parentPath = "",
  visitedFolders = new Set<string>(),
  currentDepth = 0,
  stats = { count: 0 },
): Promise<WorkspaceItem[]> {
  assertAccessToken(accessToken);

  const resolvedFolderId = await resolveFolderId(accessToken, folderId);

  if (visitedFolders.has(resolvedFolderId)) return [];
  if (currentDepth > MAX_TREE_DEPTH) return [];
  if (stats.count >= MAX_TREE_NODES) return [];

  visitedFolders.add(resolvedFolderId);

  const apiFiles = await listChildren(accessToken, resolvedFolderId);
  const items: WorkspaceItem[] = [];

  for (const rawFile of apiFiles) {
    if (stats.count >= MAX_TREE_NODES) {
      console.warn(`[Drive Tree Limit] Stopped after ${MAX_TREE_NODES} nodes.`);
      break;
    }

    const apiFile = await resolveShortcut(accessToken, rawFile);
    stats.count++;

    const isFolder = apiFile.mimeType === GOOGLE_FOLDER_MIME;
    const currentPath = parentPath ? `${parentPath}/${apiFile.name}` : `/${apiFile.name}`;

    if (isFolder) {
      let children: WorkspaceItem[] = [];

      if (currentDepth < MAX_TREE_DEPTH) {
        try {
          children = await fetchDriveFolderRecursively(
            accessToken,
            apiFile.id,
            currentPath,
            visitedFolders,
            currentDepth + 1,
            stats,
          );
        } catch (err) {
          console.warn(`[Drive Tree] Could not load children for folder ${apiFile.name}:`, err);
        }
      }

      items.push({
        id: apiFile.id,
        name: apiFile.name,
        type: "folder",
        path: currentPath,
        parentId: apiFile.parents?.[0],
        mimeType: apiFile.mimeType,
        modifiedTime: apiFile.modifiedTime,
        webViewLink: apiFile.webViewLink,
        iconLink: apiFile.iconLink,
        children,
        checked: false,
        indexingStatus: "ready",
      } as WorkspaceItem);
    } else {
      items.push({
        id: apiFile.id,
        name: apiFile.name,
        type: "file",
        path: currentPath,
        parentId: apiFile.parents?.[0],
        mimeType: apiFile.mimeType,
        modifiedTime: apiFile.modifiedTime,
        webViewLink: apiFile.webViewLink,
        iconLink: apiFile.iconLink,
        size: safeParseInt(apiFile.size),
        content: "",
        extractedImages: [],
        sourceType: getSourceType(apiFile.name, apiFile.mimeType),
        checked: false,
        indexingStatus: "idle",
      } as WorkspaceItem);
    }
  }

  items.sort((a, b) => {
    if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base", numeric: true });
  });

  return items;
}

function makeLoadedContent(args: {
  meta: GDriveAPIFile;
  resolvedName: string;
  resolvedId: string;
  path: string;
  sourceType?: DriveLoadedSourceType;
  content: string;
  images?: DriveExtractedImage[];
  warnings?: string[];
  status?: "ready" | "partial" | "failed";
}): DriveLoadedContent {
  const warnings = args.warnings || [];
  const images = args.images || [];

  return {
    fileId: args.resolvedId,
    path: args.path,
    name: args.resolvedName,
    size: safeParseInt(args.meta.size),
    mimeType: args.meta.mimeType || "",
    sourceType: args.sourceType || getSourceType(args.resolvedName, args.meta.mimeType || ""),
    content: truncateText(args.content || ""),
    images,
    extractionStatus: args.status || (warnings.length > 0 ? "partial" : "ready"),
    extractionWarnings: warnings,
  };
}

async function readGoogleWorkspaceFile(
  accessToken: string,
  meta: GDriveAPIFile,
  resolvedName: string,
  resolvedId: string,
  filePath: string,
): Promise<DriveLoadedContent> {
  const warnings: string[] = [];
  let exportMime = "text/plain";
  let sourceType: DriveLoadedSourceType = "google_doc";

  if (meta.mimeType === "application/vnd.google-apps.spreadsheet") {
    exportMime = "text/csv";
    sourceType = "spreadsheet";
  } else if (meta.mimeType === "application/vnd.google-apps.presentation") {
    exportMime = "text/plain";
    sourceType = "google_doc";
  } else if (meta.mimeType === "application/vnd.google-apps.document") {
    exportMime = "text/plain";
    sourceType = "google_doc";
  } else if (meta.mimeType === "application/vnd.google-apps.drawing") {
    sourceType = "image";
  }

  let text = "";
  const images: DriveExtractedImage[] = [];

  if (meta.mimeType === "application/vnd.google-apps.drawing") {
    // Google Drawings are visual records. Export as PNG first, then PDF fallback.
    try {
      const pngRes = await exportGoogleFile(accessToken, resolvedId, "image/png");

      if (pngRes.ok) {
        const pngBuffer = await pngRes.arrayBuffer();
        const payload = makeVisionPayload({
          buffer: pngBuffer,
          fileId: resolvedId,
          filePath,
          name: `${stripKnownExtension(resolvedName)}.png`,
          mimeType: "image/png",
          source: "drive_file",
          warnings,
        });
        if (payload) images.push(payload);
      } else {
        warnings.push(`Google Drawing PNG export failed: ${await readDriveError(pngRes)}`);
      }
    } catch (err: any) {
      warnings.push(`Google Drawing PNG export failed: ${err?.message || err}`);
    }

    if (images.length === 0) {
      try {
        const pdfRes = await exportGoogleFile(accessToken, resolvedId, "application/pdf");

        if (pdfRes.ok) {
          const pdfBuffer = await pdfRes.arrayBuffer();
          const payload = makeVisionPayload({
            buffer: pdfBuffer,
            fileId: resolvedId,
            filePath,
            name: `${stripKnownExtension(resolvedName)}.pdf`,
            mimeType: "application/pdf",
            source: "pdf_page",
            warnings,
          });
          if (payload) images.push(payload);
        } else {
          warnings.push(`Google Drawing PDF export failed: ${await readDriveError(pdfRes)}`);
        }
      } catch (err: any) {
        warnings.push(`Google Drawing PDF export failed: ${err?.message || err}`);
      }
    }

    return makeLoadedContent({
      meta,
      resolvedName,
      resolvedId,
      path: filePath,
      sourceType,
      content: "",
      images,
      warnings,
      status: images.length > 0 ? (warnings.length ? "partial" : "ready") : "failed",
    });
  }

  try {
    const contentRes = await exportGoogleFile(accessToken, resolvedId, exportMime);

    if (!contentRes.ok) {
      warnings.push(await readDriveError(contentRes));
    } else {
      text = await contentRes.text();
    }
  } catch (err: any) {
    warnings.push(`Google Workspace text export failed: ${err?.message || err}`);
  }

  // Google Docs often hold screenshots/images. Export DOCX and extract embedded media when possible.
  if (meta.mimeType === "application/vnd.google-apps.document") {
    try {
      const docxRes = await exportGoogleFile(
        accessToken,
        resolvedId,
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      );

      if (docxRes.ok) {
        const docxBuffer = await docxRes.arrayBuffer();
        const docx = await extractDocxPackage(docxBuffer, resolvedName, resolvedId, filePath);

        if (!text.trim() && docx.text) text = docx.text;
        images.push(...docx.images);
        warnings.push(...docx.warnings);
      } else {
        warnings.push(`Google Doc DOCX embedded-image export failed: ${await readDriveError(docxRes)}`);
      }
    } catch (err: any) {
      warnings.push(`Google Doc embedded image extraction failed: ${err?.message || err}`);
    }

    // If a Google Doc behaves like a scanned/visual record, attach a PDF fallback
    // so the model can use vision. This is general-purpose, not tied to warranty.
    if (!text.trim() && images.length === 0) {
      try {
        const pdfRes = await exportGoogleFile(accessToken, resolvedId, "application/pdf");

        if (pdfRes.ok) {
          const pdfBuffer = await pdfRes.arrayBuffer();
          const payload = makeVisionPayload({
            buffer: pdfBuffer,
            fileId: resolvedId,
            filePath,
            name: `${stripKnownExtension(resolvedName)}.pdf`,
            mimeType: "application/pdf",
            source: "pdf_page",
            warnings,
          });
          if (payload) images.push(payload);
        } else {
          warnings.push(`Google Doc PDF vision fallback failed: ${await readDriveError(pdfRes)}`);
        }
      } catch (err: any) {
        warnings.push(`Google Doc PDF vision fallback failed: ${err?.message || err}`);
      }
    }
  }

  // For presentations and hard visual docs, attach PDF as vision fallback when export succeeds.
  if (meta.mimeType === "application/vnd.google-apps.presentation") {
    try {
      const pdfRes = await exportGoogleFile(accessToken, resolvedId, "application/pdf");
      if (pdfRes.ok) {
        const pdfBuffer = await pdfRes.arrayBuffer();
        const payload = makeVisionPayload({
          buffer: pdfBuffer,
          fileId: resolvedId,
          filePath,
          name: `${stripKnownExtension(resolvedName)}.pdf`,
          mimeType: "application/pdf",
          source: "pdf_page",
          warnings,
        });
        if (payload) images.push(payload);
      } else {
        warnings.push(`Google presentation PDF vision export failed: ${await readDriveError(pdfRes)}`);
      }
    } catch (err: any) {
      warnings.push(`Google presentation PDF vision export failed: ${err?.message || err}`);
    }
  }

  return makeLoadedContent({
    meta,
    resolvedName,
    resolvedId,
    path: filePath,
    sourceType,
    content: normalizeExtractedText(text),
    images,
    warnings,
    status: text.trim() || images.length > 0 ? (warnings.length ? "partial" : "ready") : "failed",
  });
}

export async function downloadDriveLoadedContent(
  accessToken: string,
  fileId: string,
  fileName: string,
  filePath?: string,
): Promise<DriveLoadedContent> {
  try {
    assertAccessToken(accessToken);

    let meta = await fetchFileMetadata(accessToken, fileId);
    meta = await resolveShortcut(accessToken, meta);

    const mimeType = meta.mimeType || "";
    const resolvedName = fileName || meta.name || "Drive file";
    const resolvedId = meta.id || fileId;
    const pathForPayload = filePath || resolvedName;
    const sourceType = getSourceType(resolvedName, mimeType);

    if (mimeType.startsWith("application/vnd.google-apps.")) {
      return await readGoogleWorkspaceFile(accessToken, meta, resolvedName, resolvedId, pathForPayload);
    }

    if (isDocxFile(resolvedName, mimeType)) {
      const buffer = await downloadBinaryFile(accessToken, resolvedId);
      const extracted = await extractDocxPackage(buffer, resolvedName, resolvedId, pathForPayload);

      return makeLoadedContent({
        meta,
        resolvedName,
        resolvedId,
        path: pathForPayload,
        sourceType: "docx",
        content: extracted.text,
        images: extracted.images,
        warnings: extracted.warnings,
        status: extracted.text.trim() || extracted.images.length > 0 ? (extracted.warnings.length ? "partial" : "ready") : "failed",
      });
    }

    if (isLegacyDocFile(resolvedName, mimeType)) {
      return await readLegacyDocLikeFile(accessToken, meta, resolvedName, resolvedId, pathForPayload);
    }

    if (isPdfFile(resolvedName, mimeType)) {
      const buffer = await downloadBinaryFile(accessToken, resolvedId);
      const warnings: string[] = [];
      const images: DriveExtractedImage[] = [];

      const pdfPayload = makeVisionPayload({
        buffer,
        fileId: resolvedId,
        filePath: pathForPayload,
        name: resolvedName,
        mimeType: "application/pdf",
        source: "pdf_page",
        warnings,
      });
      if (pdfPayload) images.push(pdfPayload);

      const text = extractPdfTextNaive(buffer, resolvedName);

      return makeLoadedContent({
        meta,
        resolvedName,
        resolvedId,
        path: pathForPayload,
        sourceType: "pdf",
        content: text.startsWith("[Drive PDF text extraction note:") ? "" : text,
        images,
        warnings,
        status: images.length > 0 || text.trim() ? (warnings.length ? "partial" : "ready") : "failed",
      });
    }

    if (isImageMimeOrName(resolvedName, mimeType)) {
      const buffer = await downloadBinaryFile(accessToken, resolvedId);
      const warnings: string[] = [];
      const images: DriveExtractedImage[] = [];

      const payload = makeVisionPayload({
        buffer,
        fileId: resolvedId,
        filePath: pathForPayload,
        name: resolvedName,
        mimeType,
        source: "drive_file",
        warnings,
      });
      if (payload) images.push(payload);

      return makeLoadedContent({
        meta,
        resolvedName,
        resolvedId,
        path: pathForPayload,
        sourceType: "image",
        // Important: visual-only Drive files must not pretend they have extracted text.
        // The actual evidence is images[], which App.tsx/server.ts will pass as vision payload.
        content: "",
        images,
        warnings,
        status: images.length > 0 ? (warnings.length ? "partial" : "ready") : "failed",
      });
    }

    if (!isProbablyTextFile(resolvedName, mimeType)) {
      return makeLoadedContent({
        meta,
        resolvedName,
        resolvedId,
        path: pathForPayload,
        sourceType,
        content: `[Drive file skipped: ${resolvedName} (${mimeType || "unknown type"}) is not currently text/vision-readable. Supported readable formats include Google Docs, text/Markdown, DOCX, PDF, and standalone images.]`,
        warnings: [`Unsupported file type for Drive brain extraction: ${mimeType || "unknown type"}`],
        status: "failed",
      });
    }

    const downloadUrl =
      `${DRIVE_API_BASE}/files/${encodeURIComponent(resolvedId)}` +
      `?alt=media&supportsAllDrives=true`;

    const contentRes = await driveFetch(accessToken, downloadUrl, {
      headers: {
        Accept: "text/plain,text/markdown,text/csv,application/json,text/*,*/*",
      },
    });

    if (!contentRes.ok) {
      const err = await readDriveError(contentRes);
      return makeLoadedContent({
        meta,
        resolvedName,
        resolvedId,
        path: pathForPayload,
        sourceType: "text",
        content: `[Drive content download failed: ${err}]`,
        warnings: [err],
        status: "failed",
      });
    }

    const text = await contentRes.text();

    return makeLoadedContent({
      meta,
      resolvedName,
      resolvedId,
      path: pathForPayload,
      sourceType: "text",
      content: normalizeExtractedText(text),
      images: [],
      warnings: [],
      status: "ready",
    });
  } catch (err: any) {
    return {
      fileId,
      path: filePath || fileName || fileId,
      name: fileName || fileId,
      sourceType: "unknown",
      content: `[Exception loading Drive file contents: ${err?.message || err}]`,
      images: [],
      extractionStatus: "failed",
      extractionWarnings: [String(err?.message || err)],
    };
  }
}

export async function downloadDriveFileContents(
  accessToken: string,
  fileId: string,
  fileName: string,
): Promise<string> {
  const loaded = await downloadDriveLoadedContent(accessToken, fileId, fileName);
  return loaded.content;
}

export type DriveSearchCandidate = {
  id: string;
  name: string;
  path: string;
  type: "file" | "folder";
  size?: number;
  checked?: boolean;
  contentLoaded?: boolean;
  imageCount?: number;
  sourceType?: DriveLoadedSourceType;
  extractionStatus?: "ready" | "partial" | "failed";
  score: number;
  reasons: string[];
};

function normalizeSearchText(input: string): string {
  return String(input || "")
    .toLowerCase()
    .replace(/[’‘]/g, "'")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}


export const RODGERSPC_BLOG_IMAGE_PRIMARY_LOGO_NAME =
  "Master RodgersPC Logo Blue & Silver 2026 Transparent Background to be used in blog post OG images for branding.png";

export const RODGERSPC_BLOG_IMAGE_FALLBACK_LOGO_NAME =
  "RPC Silver LOGO 2025 for reference and creativity and to be used when the master logo colors will not work on a OG image created.png";

const RODGERSPC_PRIMARY_LOGO_SEARCH = normalizeSearchText(RODGERSPC_BLOG_IMAGE_PRIMARY_LOGO_NAME);
const RODGERSPC_FALLBACK_LOGO_SEARCH = normalizeSearchText(RODGERSPC_BLOG_IMAGE_FALLBACK_LOGO_NAME);

function isRodgerspcPrimaryBlogLogo(haystack: string): boolean {
  const text = normalizeSearchText(haystack);
  return text.includes(RODGERSPC_PRIMARY_LOGO_SEARCH) ||
    (text.includes("master rodgerspc logo") && text.includes("blue") && text.includes("silver") && text.includes("2026"));
}

function isRodgerspcFallbackBlogLogo(haystack: string): boolean {
  const text = normalizeSearchText(haystack);
  return text.includes(RODGERSPC_FALLBACK_LOGO_SEARCH) ||
    (text.includes("rpc silver logo") && text.includes("2025"));
}

function isRodgerspcLogoFolderPath(haystack: string): boolean {
  const text = normalizeSearchText(haystack);
  return text.includes("rodgerspc logos") || text.includes("logos folder") || text.includes("logo folder");
}

function isRodgerspcPastOgImagePath(haystack: string): boolean {
  const text = normalizeSearchText(haystack);
  return (
    (text.includes("blog engine") || text.includes("blog")) &&
    (
      text.includes("og post images") ||
      text.includes("post og images") ||
      text.includes("blog post og images") ||
      text.includes("og images") ||
      text.includes("approved og") ||
      text.includes("final published blog images") ||
      text.includes("existing blog images")
    )
  );
}

function levenshteinDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (!a) return b.length;
  if (!b) return a.length;

  const prev = new Array(b.length + 1).fill(0).map((_, i) => i);
  const curr = new Array(b.length + 1).fill(0);

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
  }

  return prev[b.length];
}

function tokensAreFuzzyMatch(a: string, b: string): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.length < 4 || b.length < 4) return false;
  if (a.startsWith(b) || b.startsWith(a)) return true;

  const maxLen = Math.max(a.length, b.length);
  const distance = levenshteinDistance(a, b);

  return maxLen <= 6 ? distance <= 1 : distance <= 2;
}

/**
 * Scores the already-loaded Drive tree by filename/path/content-if-present.
 * This does not call Drive and does not load file contents. It is safe for every chat turn.
 * It is global logic. It does not depend on hard-coded folders that exist today.
 */
export function scoreWorkspaceItemsForQuery(
  items: WorkspaceItem[],
  query: string,
  options: {
    includeUnfocusedFiles?: boolean;
    maxResults?: number;
  } = {},
): DriveSearchCandidate[] {
  const includeUnfocusedFiles = options.includeUnfocusedFiles ?? false;
  const maxResults = options.maxResults ?? 12;

  const stopWords = new Set([
    "the",
    "and",
    "for",
    "with",
    "you",
    "that",
    "this",
    "our",
    "are",
    "what",
    "how",
    "why",
    "about",
    "need",
    "should",
    "your",
    "from",
    "have",
    "has",
    "was",
    "were",
    "there",
    "then",
    "than",
    "into",
    "onto",
    "when",
    "where",
    "will",
    "would",
    "could",
    "tell",
    "ended",
    "doing",
    "done",
    "look",
    "lookup",
    "search",
    "file",
    "files",
    "folder",
    "folders",
    "drive",
    "log",
    "logs",
    "job",
    "jobs",
    "chat",
    "history",
    "record",
    "records",
  ]);

  const keywords = normalizeSearchText(query)
    .split(/\s+/)
    .map((w) => w.trim())
    .filter((w) => w.length > 2 && !stopWords.has(w));

  if (keywords.length === 0) return [];

  const candidates: DriveSearchCandidate[] = [];

  const visit = (nodes: WorkspaceItem[] = [], inheritedScope = false) => {
    nodes.forEach((node) => {
      const inScope = inheritedScope || Boolean(node.checked);

      if (node.type === "folder") {
        visit(node.children || [], inScope);
        return;
      }

      if (!includeUnfocusedFiles && !inScope) {
        return;
      }

      const nameNorm = normalizeSearchText(node.name || "");
      const pathNorm = normalizeSearchText(node.path || "");
      const contentNorm = normalizeSearchText(node.content || "");
      const loadedContentNorm = normalizeSearchText(node.loadedContent?.content || "");
      const nameTokens = nameNorm.split(/\s+/).filter(Boolean);
      const pathTokens = pathNorm.split(/\s+/).filter(Boolean);
      const contentHaystack = `${contentNorm} ${loadedContentNorm}`.trim();
      const reasons: string[] = [];
      let score = 0;

      for (const keyword of keywords) {
        if (nameNorm.includes(keyword)) {
          score += 50;
          reasons.push(`name:${keyword}`);
        } else if (nameTokens.some((token) => tokensAreFuzzyMatch(token, keyword))) {
          score += 40;
          reasons.push(`name-fuzzy:${keyword}`);
        }

        if (pathNorm.includes(keyword)) {
          score += 24;
          reasons.push(`path:${keyword}`);
        } else if (pathTokens.some((token) => tokensAreFuzzyMatch(token, keyword))) {
          score += 16;
          reasons.push(`path-fuzzy:${keyword}`);
        }

        if (contentHaystack) {
          let idx = contentHaystack.indexOf(keyword);
          let count = 0;
          while (idx !== -1 && count < 40) {
            count++;
            score += 3;
            idx = contentHaystack.indexOf(keyword, idx + keyword.length);
          }
          if (count > 0) reasons.push(`content:${keyword}(${count})`);
        }
      }

      const allKeywordsInPathOrName = keywords.every(
        (keyword) =>
          nameNorm.includes(keyword) ||
          pathNorm.includes(keyword) ||
          nameTokens.some((token) => tokensAreFuzzyMatch(token, keyword)) ||
          pathTokens.some((token) => tokensAreFuzzyMatch(token, keyword)),
      );

      if (allKeywordsInPathOrName && keywords.length >= 2) {
        score += 80;
        reasons.push("multi-token-name-path-match");
      }

      if (inScope || node.checked) {
        score += 10;
        reasons.push("focused");
      }

      if (node.extractedImages?.length || node.loadedContent?.images?.length) {
        score += 4;
        reasons.push("has-vision-assets");
      }

      if (score > 0) {
        candidates.push({
          id: node.id,
          name: node.name,
          path: node.path,
          type: "file",
          size: node.size,
          checked: node.checked,
          contentLoaded: Boolean(node.content || node.loadedContent?.content),
          imageCount: node.extractedImages?.length || node.loadedContent?.images?.length || 0,
          sourceType: node.sourceType || node.loadedContent?.sourceType,
          extractionStatus: node.extractionStatus || node.loadedContent?.extractionStatus,
          score,
          reasons: [...new Set(reasons)],
        });
      }
    });
  };

  visit(items, false);

  return candidates
    .sort((a, b) => b.score - a.score || a.path.localeCompare(b.path))
    .slice(0, maxResults);
}

/**
 * Recursively collects checked/gold file nodes.
 * This is intentionally content-agnostic so App.tsx can warm Drive memory in the background
 * without the sidebar pretending every file has already been reviewed.
 */
export function collectFocusedDriveFiles(items: WorkspaceItem[]): WorkspaceItem[] {
  const focusedFiles: WorkspaceItem[] = [];

  const visit = (nodes: WorkspaceItem[] = [], inheritedScope = false) => {
    for (const node of nodes) {
      const inScope = inheritedScope || Boolean(node.checked);

      if (node.type === "folder") {
        visit(node.children || [], inScope);
        continue;
      }

      if (node.type === "file" && inScope) {
        focusedFiles.push(node);
      }
    }
  };

  visit(items, false);
  return focusedFiles;
}

export function countFocusedDriveFiles(items: WorkspaceItem[]): number {
  return collectFocusedDriveFiles(items).length;
}

/**
 * Builds a compact metadata list for focused checked/gold Drive items without loading content.
 * Use this in prompts/debugging to show what Drive data is in focus.
 */
export function buildFocusedDriveMetadata(
  items: WorkspaceItem[],
  maxLines = 250,
): string {
  const lines: string[] = [];

  const visit = (nodes: WorkspaceItem[] = [], inheritedScope = false) => {
    for (const node of nodes) {
      if (lines.length >= maxLines) return;

      const inScope = inheritedScope || Boolean(node.checked);

      if (node.type === "folder") {
        if (node.checked) {
          lines.push(`- [focused folder] ${node.path}`);
        }
        visit(node.children || [], inScope);
        continue;
      }

      if (inScope) {
        const loadedText = node.content || node.loadedContent?.content ? "loaded" : "not loaded";
        const imageCount = node.extractedImages?.length || node.loadedContent?.images?.length || 0;
        const sourceType = node.sourceType || node.loadedContent?.sourceType || getSourceType(node.name, node.mimeType || "");
        lines.push(
          `- [focused file] ${node.path} | type=${sourceType} | size=${node.size || 0} | content=${loadedText} | images=${imageCount}`,
        );
      }
    }
  };

  visit(items, false);

  if (lines.length >= maxLines) {
    lines.push(`[Focused Drive metadata capped at ${maxLines} lines.]`);
  }

  return lines.join("\n");
}

// Backward-compatible alias while older files are being replaced.
export const buildDriveScopeMetadata = buildFocusedDriveMetadata;

function base64PayloadChars(image: DriveExtractedImage): number {
  return String(image?.base64 || "").length;
}

function driveLoadedContentPriorityScore(item: DriveLoadedContent | WorkspaceItem, query = ""): number {
  const q = normalizeSearchText(query);
  const name = normalizeSearchText((item as any).name || "");
  const pathText = normalizeSearchText((item as any).path || "");
  const body = normalizeSearchText((item as any).content || (item as any).loadedContent?.content || "");
  const sourceType = String((item as any).sourceType || (item as any).loadedContent?.sourceType || "").toLowerCase();

  let score = 0;

  const namePath = `${(item as any).path || ""} ${(item as any).name || ""}`;
  const isBrandKit = pathText.includes("brand kit") || pathText.includes("000 rodgerspc brand");
  const isLogoFolder = isRodgerspcLogoFolderPath(namePath);
  const isPastOgImage = isRodgerspcPastOgImagePath(namePath);
  const isPrimaryBlogLogo = isRodgerspcPrimaryBlogLogo(namePath);
  const isFallbackBlogLogo = isRodgerspcFallbackBlogLogo(namePath);
  const hasVisualAsset = sourceType === "image" || sourceType === "pdf" || Number((item as any).loadedContent?.images?.length || (item as any).images?.length || 0) > 0;

  if (pathText.includes("source of truth") || pathText.includes("000 rodgerspc source")) score += 500;

  if (isPrimaryBlogLogo && hasVisualAsset) score += 5200;
  if (isFallbackBlogLogo && hasVisualAsset) score += 5000;
  if (isPastOgImage && hasVisualAsset) score += 4200;
  if (isLogoFolder && hasVisualAsset) score += 3600;
  if (isBrandKit && hasVisualAsset) score += 1100;
  if (pathText.includes("approved og") || pathText.includes("final published blog images")) score += 1050;
  if (pathText.includes("logo") || name.includes("logo")) score += (isBrandKit || isLogoFolder) ? 980 : 260;
  if (pathText.includes("website screen") || pathText.includes("website screenshot") || pathText.includes("screenshot")) score += isBrandKit ? 920 : 190;
  if (pathText.includes("homepage hero") || pathText.includes("about page") || pathText.includes("meet the owner") || pathText.includes("services")) score += isBrandKit ? 820 : 150;
  if (pathText.includes("coin") || name.includes("coin") || pathText.includes("badge") || name.includes("badge")) score += isBrandKit ? 760 : 180;
  if (pathText.includes("mascot") || name.includes("mascot")) score += isBrandKit ? 650 : 180;

  if (pathText.includes("brand kit")) score += 460;
  if (pathText.includes("brand rules") || name.includes("brand rules")) score += 420;
  if (pathText.includes("image prompt") || name.includes("image prompt")) score += 390;
  if (pathText.includes("og image") || name.includes("og image")) score += 360;
  if (pathText.includes("blog formatting") || name.includes("blog formatting")) score += 340;
  if (pathText.includes("final published blog post") || pathText.includes("published blog")) score += 300;
  if (pathText.includes("blog engine")) score += 260;

  if (sourceType === "google_doc" || sourceType === "text" || sourceType === "docx") score += 60;
  if (hasVisualAsset) score += 140;

  if (q) {
    const keywords = q.split(/\s+/).filter((w) => w.length > 2);
    for (const keyword of keywords) {
      if (name.includes(keyword)) score += 80;
      if (pathText.includes(keyword)) score += 50;
      if (body.includes(keyword)) score += 8;
    }
  }

  const size = Number((item as any).size || 0);
  if (size > 8 * 1024 * 1024) score -= 80;
  else if (size > 3 * 1024 * 1024) score -= 30;

  return score;
}

function compactDriveLoadedContent(
  item: DriveLoadedContent,
  options: {
    maxTextChars?: number;
    maxVisualPayloads?: number;
    maxSingleBase64Chars?: number;
    maxTotalBase64Chars?: number;
  } = {},
): DriveLoadedContent {
  const maxTextChars = options.maxTextChars ?? MAX_FILE_TEXT_CHARS;
  const maxVisualPayloads = options.maxVisualPayloads ?? MAX_CHAT_PROMPT_VISUAL_PAYLOAD_COUNT;
  const maxSingleBase64Chars = options.maxSingleBase64Chars ?? MAX_CHAT_PROMPT_SINGLE_BASE64_CHARS;
  const maxTotalBase64Chars = options.maxTotalBase64Chars ?? MAX_CHAT_PROMPT_TOTAL_BASE64_CHARS;

  const warnings = [...(item.extractionWarnings || [])];
  const originalText = String(item.content || "");
  const compactText = truncateText(originalText, maxTextChars);

  if (originalText.length > compactText.length) {
    warnings.push(`Drive payload compacted for chat request: text trimmed from ${originalText.length} to ${compactText.length} characters.`);
  }

  const keptImages: DriveExtractedImage[] = [];
  let totalBase64Chars = 0;

  for (const image of item.images || []) {
    if (keptImages.length >= maxVisualPayloads) {
      warnings.push(`Drive payload compacted for chat request: visual payload count capped at ${maxVisualPayloads}.`);
      break;
    }

    const chars = base64PayloadChars(image);
    if (chars > maxSingleBase64Chars) {
      warnings.push(`Skipped inline visual ${image.name || "image"} from ${item.name}: base64 payload too large for chat request.`);
      continue;
    }

    if (totalBase64Chars + chars > maxTotalBase64Chars) {
      warnings.push(`Skipped inline visual ${image.name || "image"} from ${item.name}: total visual payload budget reached.`);
      continue;
    }

    keptImages.push(image);
    totalBase64Chars += chars;
  }

  if ((item.images || []).length > keptImages.length) {
    warnings.push(`Drive payload compacted for chat request: kept ${keptImages.length} of ${(item.images || []).length} visual payload(s).`);
  }

  return {
    ...item,
    content: compactText,
    images: keptImages,
    extractionWarnings: [...new Set(warnings)],
    extractionStatus: item.extractionStatus === "failed" ? "failed" : warnings.length ? "partial" : item.extractionStatus,
  };
}

/**
 * Builds a bounded chat-ready Drive evidence package from an already-loaded/focused pool.
 *
 * This does NOT reduce the checked/gold Drive scope. It only limits what is sent inline
 * to the model on a single chat turn, preventing HTTP 413 as RodgersPC Drive grows.
 *
 * App.tsx should use this immediately before POSTing /api/chat.
 */
export function buildDriveChatPayload(
  activeContext: DriveLoadedContent[] = [],
  query = "",
  options: {
    maxFiles?: number;
    maxTotalTextChars?: number;
    maxVisualPayloads?: number;
    maxSingleBase64Chars?: number;
    maxTotalBase64Chars?: number;
  } = {},
): DriveLoadedContent[] {
  const maxFiles = options.maxFiles ?? MAX_CHAT_PROMPT_FILE_COUNT;
  const maxTotalTextChars = options.maxTotalTextChars ?? MAX_CHAT_PROMPT_TOTAL_TEXT_CHARS;
  const maxVisualPayloads = options.maxVisualPayloads ?? MAX_CHAT_PROMPT_VISUAL_PAYLOAD_COUNT;
  const maxSingleBase64Chars = options.maxSingleBase64Chars ?? MAX_CHAT_PROMPT_SINGLE_BASE64_CHARS;
  const maxTotalBase64Chars = options.maxTotalBase64Chars ?? MAX_CHAT_PROMPT_TOTAL_BASE64_CHARS;

  const sorted = [...(activeContext || [])]
    .filter(Boolean)
    .sort((a, b) => driveLoadedContentPriorityScore(b, query) - driveLoadedContentPriorityScore(a, query));

  const result: DriveLoadedContent[] = [];
  let totalTextChars = 0;
  let totalVisualPayloads = 0;
  let totalBase64Chars = 0;

  for (const item of sorted) {
    if (result.length >= maxFiles) break;

    const remainingText = Math.max(0, maxTotalTextChars - totalTextChars);
    if (remainingText <= 0 && totalVisualPayloads >= maxVisualPayloads) break;

    const remainingVisualCount = Math.max(0, maxVisualPayloads - totalVisualPayloads);
    const remainingBase64 = Math.max(0, maxTotalBase64Chars - totalBase64Chars);

    const compacted = compactDriveLoadedContent(item, {
      maxTextChars: Math.min(MAX_FILE_TEXT_CHARS, remainingText || 0),
      maxVisualPayloads: remainingVisualCount,
      maxSingleBase64Chars,
      maxTotalBase64Chars: remainingBase64,
    });

    const hasText = String(compacted.content || "").trim().length > 0;
    const hasImages = (compacted.images || []).length > 0;

    if (!hasText && !hasImages) continue;

    result.push(compacted);
    totalTextChars += String(compacted.content || "").length;
    totalVisualPayloads += (compacted.images || []).length;
    totalBase64Chars += (compacted.images || []).reduce((sum, image) => sum + base64PayloadChars(image), 0);
  }

  if (sorted.length > result.length && result.length > 0) {
    const last = result[result.length - 1];
    last.extractionWarnings = [
      ...(last.extractionWarnings || []),
      `Drive chat payload capped: sent ${result.length} of ${sorted.length} loaded/focused file(s). Full checked Drive scope remains available for retrieval, but was not all sent inline to prevent HTTP 413.`,
    ];
    if (last.extractionStatus !== "failed") last.extractionStatus = "partial";
  }

  return result;
}

/**
 * Returns the focused file nodes ranked for chat loading. This keeps scope large,
 * but gives App.tsx a safe way to load the most useful files first instead of
 * attempting to inline every checked file.
 */
function queryNeedsBrandKitVisuals(query: string): boolean {
  const q = normalizeSearchText(query);

  if (!q) return false;

  return [
    "blog",
    "og image",
    "open graph",
    "image",
    "generate image",
    "make image",
    "render image",
    "visual",
    "brand",
    "brand kit",
    "logo",
    "mascot",
    "website",
    "web creative",
    "flyer",
    "ad",
    "landing page",
    "google business",
    "facebook",
    "nextdoor",
    "wix",
    "design",
    "creative",
  ].some((signal) => q.includes(signal));
}

function isBlogCreativeVisualWorkspaceItem(item: WorkspaceItem): boolean {
  const haystack = `${item.path || ""} ${item.name || ""}`;
  const text = normalizeSearchText(haystack);
  const sourceType = String(item.sourceType || item.loadedContent?.sourceType || getSourceType(item.name, item.mimeType || "") || "").toLowerCase();

  const looksVisual =
    sourceType === "image" ||
    sourceType === "pdf" ||
    text.includes("logo") ||
    text.includes("mascot") ||
    text.includes("coin") ||
    text.includes("badge") ||
    text.includes("website screen") ||
    text.includes("website screenshot") ||
    text.includes("homepage hero") ||
    text.includes("approved og") ||
    text.includes("final published blog images") ||
    text.includes("og image");

  return looksVisual && (
    text.includes("brand kit") ||
    text.includes("000 rodgerspc brand") ||
    isRodgerspcLogoFolderPath(haystack) ||
    isRodgerspcPastOgImagePath(haystack) ||
    isRodgerspcPrimaryBlogLogo(haystack) ||
    isRodgerspcFallbackBlogLogo(haystack)
  );
}

/**
 * Returns the focused file nodes ranked for chat loading. This keeps scope large,
 * but gives App.tsx a safe way to load the most useful files first instead of
 * attempting to inline every checked file.
 *
 * Blog/Web image work needs a real visual reference pack, not just Brand Kit text.
 * This forces a small number of RodgersPC Brand Kit visual files into the front of
 * the loading list so the server can pass actual logo/site/OG reference images to Gemini.
 */
export function collectFocusedDriveFilesForChat(
  items: WorkspaceItem[],
  query = "",
  maxFiles = MAX_CHAT_PROMPT_FILE_COUNT,
): WorkspaceItem[] {
  const focused = collectFocusedDriveFiles(items);
  const sorted = focused
    .sort((a, b) => driveLoadedContentPriorityScore(b, query) - driveLoadedContentPriorityScore(a, query));

  if (!queryNeedsBrandKitVisuals(query)) {
    return sorted.slice(0, maxFiles);
  }

  const creativeVisuals = sorted.filter(isBlogCreativeVisualWorkspaceItem).slice(0, 16);
  const creativeVisualIds = new Set(creativeVisuals.map((item) => item.id));
  const rest = sorted.filter((item) => !creativeVisualIds.has(item.id));

  return [...creativeVisuals, ...rest].slice(0, maxFiles);
}


type DriveUploadResult = {
  id: string;
  name: string;
  webViewLink?: string;
  mimeType?: string;
  size?: string;
  parents?: string[];
  modifiedTime?: string;
};

function normalizeUploadFileName(fileName: string): string {
  const cleaned = String(fileName || "")
    .replace(/[\\/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return cleaned || `RodgersPC Upload ${new Date().toISOString().slice(0, 10)}.txt`;
}

function inferUploadMimeType(fileName: string, suppliedMime = "text/plain"): string {
  const lower = fileName.toLowerCase();
  const supplied = String(suppliedMime || "").trim();

  if (supplied && supplied !== "application/octet-stream") return supplied;

  if (lower.endsWith(".pdf")) return "application/pdf";
  if (lower.endsWith(".docx")) return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (lower.endsWith(".doc")) return "application/msword";
  if (lower.endsWith(".xlsx")) return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (lower.endsWith(".xls")) return "application/vnd.ms-excel";
  if (lower.endsWith(".pptx")) return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
  if (lower.endsWith(".ppt")) return "application/vnd.ms-powerpoint";
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".webp")) return "image/webp";
  if (lower.endsWith(".gif")) return "image/gif";
  if (lower.endsWith(".html") || lower.endsWith(".htm")) return "text/html";
  if (lower.endsWith(".json")) return "application/json";
  if (lower.endsWith(".csv")) return "text/csv";
  if (lower.endsWith(".md")) return "text/markdown";

  return "text/plain";
}

function dataUrlToBlob(dataUrl: string): { blob: Blob; mimeType: string; byteLength: number } | null {
  const matches = String(dataUrl || "").match(/^data:([^;]+);base64,(.*)$/);

  if (!matches) return null;

  const mimeType = matches[1] || "application/octet-stream";
  const base64Data = matches[2] || "";
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const bytes = new Uint8Array(byteNumbers);

  return {
    blob: new Blob([bytes], { type: mimeType }),
    mimeType,
    byteLength: bytes.byteLength,
  };
}

function isGoogleDocsImportName(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return lower.endsWith(".doc") || lower.endsWith(".docx") || lower.endsWith(".html") || lower.endsWith(".htm");
}

async function verifyUploadedDriveFile(
  accessToken: string,
  fileId: string,
  expectedName: string,
): Promise<DriveUploadResult> {
  let lastError: unknown = null;

  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      const params = new URLSearchParams({
        fields: "id,name,mimeType,size,parents,modifiedTime,webViewLink",
        supportsAllDrives: "true",
      });

      const response = await driveFetch(
        accessToken,
        `${DRIVE_API_BASE}/files/${encodeURIComponent(fileId)}?${params.toString()}`,
      );

      if (!response.ok) {
        throw new Error(await readDriveError(response));
      }

      const metadata = (await response.json()) as DriveUploadResult;

      if (!metadata?.id) {
        throw new Error("Drive upload verification failed: Google returned no file id.");
      }

      if (metadata.name && expectedName && metadata.name !== expectedName) {
        console.warn("[Drive Upload] Uploaded file name differs after Drive processing", {
          expectedName,
          actualName: metadata.name,
        });
      }

      return metadata;
    } catch (err) {
      lastError = err;
      await new Promise((resolve) => setTimeout(resolve, 350 * attempt));
    }
  }

  throw new Error(`Drive upload created but verification failed: ${lastError instanceof Error ? lastError.message : String(lastError)}`);
}

export async function uploadFileToDriveFolder(
  accessToken: string,
  folderId: string,
  fileName: string,
  contentStr: string,
  fileMimeType = "text/plain",
): Promise<DriveUploadResult> {
  assertAccessToken(accessToken);

  const resolvedFolderId = await resolveFolderId(accessToken, folderId);
  const safeFileName = normalizeUploadFileName(fileName);
  let finalMime = inferUploadMimeType(safeFileName, fileMimeType);
  let rawBody: Blob | string = contentStr || "";

  const dataUrlPayload = typeof contentStr === "string" && contentStr.startsWith("data:")
    ? dataUrlToBlob(contentStr)
    : null;

  if (dataUrlPayload) {
    finalMime = dataUrlPayload.mimeType || finalMime;
    rawBody = dataUrlPayload.blob;
  }

  const metadata: any = {
    name: safeFileName,
    parents: [resolvedFolderId],
  };

  /**
   * Import HTML / DOC / DOCX into native Google Docs only when that is intentional.
   * This helps the exporter keep editable Drive documents, including inline HTML images
   * when the exporter supplies an HTML body.
   *
   * Non-document uploads such as PDF/images stay as real binary files. That prevents the
   * UI from showing a fake upload while Drive silently rejects or mangles the payload.
   */
  if (isGoogleDocsImportName(safeFileName)) {
    metadata.mimeType = "application/vnd.google-apps.document";

    if (!dataUrlPayload && finalMime === "text/plain") {
      finalMime = "text/html";
      rawBody = `<!doctype html><html><head><meta charset="utf-8"></head><body><pre style="font-family: Arial, sans-serif; white-space: pre-wrap;">${escapeHtml(
        String(rawBody || ""),
      )}</pre></body></html>`;
    }
  }

  const contentBlob = rawBody instanceof Blob ? rawBody : new Blob([rawBody], { type: finalMime });

  if (contentBlob.size === 0) {
    throw new Error("Drive upload blocked: file payload is empty.");
  }

  const boundary = `rpc_boundary_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  const delimiter = `\r\n--${boundary}\r\n`;
  const closeDelimiter = `\r\n--${boundary}--`;

  const metadataPart =
    `${delimiter}` +
    `Content-Type: application/json; charset=UTF-8\r\n\r\n` +
    `${JSON.stringify(metadata)}`;

  const mediaHeader =
    `${delimiter}` +
    `Content-Type: ${finalMime}\r\n\r\n`;

  const closingPart = `\r\n${closeDelimiter}`;

  const part2Buffer = await contentBlob.arrayBuffer();

  const encoder = new TextEncoder();
  const part1Bytes = encoder.encode(metadataPart + mediaHeader);
  const part3Bytes = encoder.encode(closingPart);

  const finalBytes = new Uint8Array(
    part1Bytes.byteLength + part2Buffer.byteLength + part3Bytes.byteLength,
  );

  finalBytes.set(part1Bytes, 0);
  finalBytes.set(new Uint8Array(part2Buffer), part1Bytes.byteLength);
  finalBytes.set(part3Bytes, part1Bytes.byteLength + part2Buffer.byteLength);

  const uploadParams = new URLSearchParams({
    uploadType: "multipart",
    supportsAllDrives: "true",
    fields: "id,name,mimeType,size,parents,modifiedTime,webViewLink",
  });

  const response = await driveFetch(
    accessToken,
    `${DRIVE_UPLOAD_BASE}/files?${uploadParams.toString()}`,
    {
      method: "POST",
      headers: {
        "Content-Type": `multipart/related; boundary=${boundary}`,
      },
      body: finalBytes,
    },
  );

  if (!response.ok) {
    const err = await readDriveError(response);
    console.error("Google Drive multipart upload failed:", err);
    throw new Error(err);
  }

  const created = (await response.json()) as DriveUploadResult;

  if (!created?.id) {
    throw new Error("Google Drive upload failed: no file id returned.");
  }

  return await verifyUploadedDriveFile(accessToken, created.id, safeFileName);
}

function escapeHtml(input: string): string {
  return String(input || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
